import { C } from '../../theme/palette';

/**
 * Des choix en pastilles, comme la maquette : un seul (radio) ou plusieurs
 * (cases), la pastille choisie bordée de la couleur.
 *
 * @param {object} props propriétés
 * @param {string} props.titre nom du groupe, pour les lecteurs d'écran
 * @param {Array<[string|number, string]>} props.options valeur et libellé de chaque pastille
 * @param {string|number|Array} props.valeur la valeur choisie, ou les valeurs (plusieurs)
 * @param {(v: *) => void} props.onChange appelé avec la valeur touchée
 * @param {boolean} [props.plusieurs] plusieurs choix à la fois
 * @param {string} [props.couleur] couleur de la pastille choisie
 * @param {boolean} [props.petites] pastilles carrées (jours de la semaine)
 * @returns {import('react').ReactElement} le groupe
 */
export function Puces({ titre, options, valeur, onChange, plusieurs = false, couleur, petites }) {
  const teinte = couleur || C.teal;
  const choisie = (v) => (plusieurs ? (valeur || []).includes(v) : valeur === v);
  return (
    <div
      role={plusieurs ? 'group' : 'radiogroup'}
      aria-label={titre}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}
    >
      {options.map(([v, libelle]) => {
        const on = choisie(v);
        return (
          <button
            key={String(v)}
            type="button"
            role={plusieurs ? 'checkbox' : 'radio'}
            aria-checked={on}
            onClick={() => onChange(v)}
            style={{
              border: '2px solid ' + (on ? teinte : C.border),
              background: on ? (couleur === C.purple ? C.purpleL : C.tealL) : C.white,
              color: on ? teinte : C.ink,
              borderRadius: 10,
              padding: petites ? '6px 0' : '8px 12px',
              minWidth: petites ? 34 : undefined,
              fontSize: 11.5,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            {libelle}
          </button>
        );
      })}
    </div>
  );
}

/** Petit titre de groupe, en capitales grises (« LIEU HABITUEL »). */
export function Etiquette({ children }) {
  return (
    <div
      style={{
        fontSize: 9,
        fontWeight: 800,
        color: C.muted,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        margin: '4px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

/** Bandeau de constat : vert (sur place), ambre (à vérifier), gris (info). */
export function Constat({ ton = 'ok', children }) {
  const tons = {
    ok: [C.greenL, C.green],
    alerte: [C.amberL, C.amber],
    info: [C.navyXL, C.grey],
  };
  const [fond, encre] = tons[ton] || tons.info;
  return (
    <div
      role="status"
      style={{
        background: fond,
        color: encre,
        borderRadius: 10,
        padding: '9px 11px',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.45,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}
